import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { CommunityRow } from '../../components/CommunityRow';
import { CreateFab } from '../../components/CreateFab';
import { PageHeader } from '../../components/PageHeader';
import { PostCard } from '../../components/PostCard';
import { PulseRail } from '../../components/PulseRail';
import { SegmentedTabs } from '../../components/SegmentedTabs';
import { SectionTitle } from '../../components/ui/Card';
import { EmptyState } from '../../components/ui/EmptyState';
import { communities } from '../../data/communities';
import { useLocale } from '../../contexts/LocaleContext';
import { useSocial } from '../../contexts/SocialContext';
import { Explore } from '../Explore';
import { useScreenInit } from '../../useScreenInit.js';
type Tab = 'feed' | 'communities' | 'discover';

/**
 * MOB/Community/Home — SOCIAL.
 * Pulse + feed, interest communities, and discovery (search, people, topics,
 * trending) live here, never on Home.
 */
export function CommunityHome({
  initialTab = 'feed'
}: {
  initialTab?: Tab;
}) {
  const {
    t
  } = useLocale();
  const navigate = useNavigate();
  const social = useSocial();
  const screenInit = useScreenInit();
  const [tab, setTab] = useState<Tab>(screenInit?.communityTab as Tab | undefined ?? initialTab);
  const mine = communities.filter(community => social.isMember(community.id));
  const others = communities.filter(community => !social.isMember(community.id));
  return <div className="pb-2" data-id="element-2509" data-mp="1fhl2g8taijma">
      <PageHeader title={t('community.title')} subtitle={t('community.subtitle')} action={<button type="button" onClick={() => navigate('/busca')} aria-label={t('search.title')} className="grid h-11 w-11 shrink-0 place-items-center rounded-pill text-deep transition-colors duration-150 ease-brand hover:bg-sand-deep" data-id="element-2511" data-mp="1kbp9st6gwtnw">
            <SearchIcon size={19} data-id="element-2512" data-mp="1y6rljwxzxd8m" />
          </button>} data-id="element-2510" data-mp="1fgs4nxcb74ga" />

      <div className="px-2" data-id="element-2513" data-mp="3ucs9qxl7wc5i">
        <SegmentedTabs label={t('community.title')} value={tab} onChange={setTab} tabs={[{
        id: 'feed',
        label: t('community.tab.feed')
      }, {
        id: 'communities',
        label: t('community.tab.communities')
      }, {
        id: 'discover',
        label: t('community.tab.discover')
      }]} data-id="element-2514" data-mp="3u9wb171jpjg0" />
      </div>

      <div className="mt-3 px-2" data-id="element-2515" data-mp="htao06pq8h6d">
        {tab === 'feed' && <>
            <section data-id="element-2516" data-mp="cupne66kbvhe">
              <SectionTitle data-id="element-2517" data-mp="cvhhtdofbn63">{t('pulse.title')}</SectionTitle>
              <PulseRail pulses={social.pulses} data-id="element-2518" data-mp="qvs92htnb3rs" />
            </section>
            <section className="mt-3" data-id="element-2519" data-mp="lwfmly6orypq">
              <SectionTitle data-id="element-2520" data-mp="lxifucrhed8t">{t('community.feed.posts')}</SectionTitle>
              <ul className="space-y-3" data-id="element-2521" data-mp="2smn5nbvof6zk">
                {social.posts.map(post => <li key={post.id} data-id="element-2522" data-mp="2nntvntflly45">
                    <PostCard post={post} data-id="element-2523" data-mp="2novggpmf1yz2" />
                  </li>)}
              </ul>
            </section>
          </>}

        {tab === 'communities' && <>
            <section data-id="element-2524" data-mp="183t7obftv8o2">
              <SectionTitle data-id="element-2525" data-mp="184cm3i69rexc">{t('community.mine')}</SectionTitle>
              {mine.length === 0 ? <EmptyState title={t('community.none.title')} body={t('community.none.body')} data-id="element-2526" data-mp="ud7lodv2uily" /> : <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-2527" data-mp="ke8lljodnvak">
                  {mine.map(community => <CommunityRow key={community.id} community={community} data-id="element-2528" data-mp="pfo537awptqu" />)}
                </ul>}
            </section>
            {others.length > 0 && <section className="mt-3" data-id="element-2529" data-mp="fggvmv7amj90">
                <SectionTitle data-id="element-2530" data-mp="25sw916c8tjna">{t('community.discover')}</SectionTitle>
                <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-2531" data-mp="25qidyxhkc7fq">
                  {others.map(community => <CommunityRow key={community.id} community={community} data-id="element-2532" data-mp="1l43s1quc8yrx" />)}
                </ul>
              </section>}
          </>}

        {tab === 'discover' && <Explore embedded data-id="element-2533" data-mp="1b4v0f4my3w8q" />}
      </div>

      <CreateFab data-id="element-2534" data-mp="1b3tbbw93h980" />
    </div>;
}